
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LeftNavigation: React.FC = () => {
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [retrofitProgress, setRetrofitProgress] = useState(0);
  const [activeConversions, setActiveConversions] = useState(0);
  const [energyLevel, setEnergyLevel] = useState(0);
  const [particleCount, setParticleCount] = useState(0);

  useEffect(() => {
    setIsLoaded(true);

    // Real-time retrofit progress animation
    const progressInterval = setInterval(() => {
      setRetrofitProgress(prev => (prev + 1) % 101);
    }, 100);

    // Active conversions counter
    const conversionsInterval = setInterval(() => {
      setActiveConversions(Math.floor(Math.random() * 8) + 5);
    }, 2000);

    // Energy level fluctuation
    const energyInterval = setInterval(() => {
      setEnergyLevel(Math.floor(Math.random() * 30) + 70);
    }, 1500);

    // Particle animation counter
    const particleInterval = setInterval(() => {
      setParticleCount(prev => (prev + 1) % 360);
    }, 50);

    return () => {
      clearInterval(progressInterval);
      clearInterval(conversionsInterval);
      clearInterval(energyInterval);
      clearInterval(particleInterval);
    };
  }, []);

  const navigationItems = [
    { path: '/', label: 'EV Retrofitting', icon: 'ri-car-line', color: 'from-orange-500 to-red-500' },
    { path: '/about', label: 'About', icon: 'ri-information-line', color: 'from-blue-500 to-cyan-500' },
    { path: '/products', label: 'Products', icon: 'ri-heart-line', color: 'from-purple-500 to-pink-500' },
    { path: '/shop', label: 'Shop', icon: 'ri-shopping-bag-line', color: 'from-green-500 to-emerald-500' },
    { path: '/dealers', label: 'Dealers', icon: 'ri-map-pin-line', color: 'from-yellow-500 to-orange-500' },
    { path: '/faq', label: 'FAQ', icon: 'ri-question-line', color: 'from-indigo-500 to-purple-500' },
    { path: '/contact', label: 'Contact', icon: 'ri-phone-line', color: 'from-teal-500 to-cyan-500' },
  ];

  return (
    <div className="fixed left-0 top-0 h-full w-64 bg-gradient-to-br from-slate-900 via-gray-900 to-black shadow-2xl z-50 border-r border-gray-700/50 flex flex-col">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-4 left-4 w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-20 right-4 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-2xl animate-bounce-slow"></div>
        <div className="absolute bottom-16 left-2 w-14 h-14 bg-gradient-to-r from-green-500 to-teal-500 rounded-full blur-3xl animate-float-slow"></div>
      </div>

      {/* Header Section */}
      <div className={`relative p-3 border-b border-gray-700/50 backdrop-blur-sm transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
        <div className="flex items-center justify-center mb-3">
          <div className="relative group">
            {/* Simple Logo Container - Increased Size */}
            <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-orange-500/30 flex items-center justify-center transform group-hover:scale-105 transition-all duration-300 overflow-hidden">
              {/* Logo Image - Much Larger and Fully Visible */}
              <div className="relative w-20 h-20 flex items-center justify-center z-10">
                <img 
                  src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/6254aabe12a46707e114a7938f8ea221.png" 
                  alt="DJT PowerTech Logo" 
                  className="w-full h-full object-contain"
                  style={{ 
                    filter: 'brightness(1.1) contrast(1.1)',
                  }}
                />
              </div>
              
              {/* Simple Status Indicator */}
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border border-gray-900">
                <div className="w-1 h-1 bg-white rounded-full mx-auto mt-1"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center space-y-1">
          <h1 className="text-base font-bold bg-gradient-to-r from-orange-400 via-red-400 to-orange-400 bg-clip-text text-transparent">
            DJT RETROFITTING
          </h1>
          
          {/* Live Status Bar */}
          <div className="p-1.5 bg-gray-800/50 rounded-lg border border-gray-700/50 backdrop-blur-sm">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">System Status</span>
              <div className="flex items-center space-x-1">
                <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-medium">Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <div className="p-2 space-y-1 flex-1">
        {navigationItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          return (
            <div
              key={item.path}
              className={`transform transition-all duration-500 ${isLoaded ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Link
                to={item.path}
                className={`group relative flex items-center px-2 py-2 rounded-xl transition-all duration-300 overflow-hidden ${
                  isActive
                    ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-white shadow-lg border border-orange-500/30'
                    : 'text-gray-300 hover:bg-gray-800/50 hover:text-white border border-transparent hover:border-gray-700/50'
                }`}
              >
                {/* Background Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-xl`}></div>
                
                {/* Icon Container */}
                <div className={`relative w-6 h-6 rounded-lg flex items-center justify-center mr-2 transition-all duration-300 ${
                  isActive 
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg' 
                    : 'bg-gray-800/50 group-hover:bg-gray-700/70 group-hover:scale-110'
                }`}>
                  <i className={`${item.icon} text-xs transition-transform duration-300 group-hover:scale-110 ${isActive ? 'animate-pulse' : ''}`}></i>
                  
                  {/* Icon Glow */}
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg blur-md opacity-50 animate-pulse"></div>
                  )}
                </div>
                
                {/* Label */}
                <span className="font-medium text-xs tracking-wide flex-1">{item.label}</span>
                
                {/* Active Indicator */}
                {isActive && (
                  <div className="flex items-center space-x-1">
                    <div className="w-1 h-1 bg-orange-400 rounded-full animate-ping"></div>
                    <div className="w-0.5 h-3 bg-gradient-to-b from-orange-400 to-red-500 rounded-full"></div>
                  </div>
                )}
                
                {/* Hover Arrow */}
                <div className={`transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0'}`}>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      {/* Real-time Retrofitting Animation Section */}
      <div className={`p-2 transform transition-all duration-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl p-2 backdrop-blur-sm border border-gray-700/30 relative overflow-hidden">
          {/* Animated Particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-0.5 h-0.5 bg-orange-400 rounded-full animate-ping"
                style={{
                  left: `${(i * 7 + Math.sin(particleCount * 0.05 + i) * 20) % 100}%`,
                  top: `${(i * 11 + Math.cos(particleCount * 0.03 + i) * 20) % 100}%`,
                  opacity: 0.3 + Math.sin(particleCount * 0.02 + i) * 0.3,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${2 + i * 0.3}s`
                }}
              />
            ))}
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-2 relative z-10">
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center animate-pulse">
                <i className="ri-tools-line text-white text-xs"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-xs">Live Retrofitting</h3>
                <div className="flex items-center gap-1">
                  <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-xs">Active Now</span>
                </div>
              </div>
            </div>
          </div>

          {/* Real-time Progress Bar */}
          <div className="mb-2 relative z-10">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Progress</span>
              <span className="text-orange-400 font-bold">{retrofitProgress}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 rounded-full transition-all duration-300 relative"
                style={{ width: `${retrofitProgress}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                <div className="absolute right-0 top-0 w-1 h-full bg-white/50 animate-ping"></div>
              </div>
            </div>
          </div>

          {/* Live Stats Grid */}
          <div className="grid grid-cols-2 gap-1 mb-2 relative z-10">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-1.5">
              <div className="flex items-center justify-between mb-0.5">
                <i className="ri-car-line text-blue-400 text-xs"></i>
                <div className="w-0.5 h-0.5 bg-blue-400 rounded-full animate-pulse"></div>
              </div>
              <div className="text-blue-400 font-bold text-xs">{activeConversions}</div>
              <div className="text-blue-300 text-xs">Units</div>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-1.5">
              <div className="flex items-center justify-between mb-0.5">
                <i className="ri-battery-charge-line text-green-400 text-xs"></i>
                <div className="w-0.5 h-0.5 bg-green-400 rounded-full animate-bounce"></div>
              </div>
              <div className="text-green-400 font-bold text-xs">{energyLevel}%</div>
              <div className="text-green-300 text-xs">Energy</div>
            </div>
          </div>

          {/* Animated Vehicle Visualization */}
          <div className="relative z-10 bg-gray-900/50 rounded-lg p-2 border border-gray-700/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-400 text-xs">Current Process</span>
              <div className="flex items-center gap-0.5">
                <div className="w-0.5 h-0.5 bg-orange-400 rounded-full animate-ping"></div>
                <div className="w-0.5 h-0.5 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-0.5 h-0.5 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
            
            {/* Vehicle Icon with Animation */}
            <div className="relative h-8 flex items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center transform transition-all duration-500"
                  style={{ 
                    transform: `scale(${1 + Math.sin(particleCount * 0.05) * 0.1}) rotate(${Math.sin(particleCount * 0.02) * 5}deg)`,
                    opacity: 0.8 + Math.sin(particleCount * 0.03) * 0.2
                  }}
                >
                  <i className="ri-truck-line text-white text-sm"></i>
                </div>
              </div>

              {/* Energy Flow Lines */}
              <div className="absolute inset-0">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-0.5 h-0.5 bg-orange-400 rounded-full"
                    style={{
                      left: `${50 + Math.cos((particleCount + i * 120) * 0.05) * 30}%`,
                      top: `${50 + Math.sin((particleCount + i * 120) * 0.05) * 30}%`,
                      opacity: 0.5 + Math.sin(particleCount * 0.03 + i) * 0.5,
                      transform: `scale(${1 + Math.sin(particleCount * 0.04 + i) * 0.5})`
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Process Steps */}
            <div className="mt-1 space-y-0.5">
              {['Engine Removal', 'Battery Install', 'Motor Setup'].map((step, index) => (
                <div key={index} className="flex items-center gap-1">
                  <div className={`w-0.5 h-0.5 rounded-full ${
                    retrofitProgress > (index + 1) * 30 
                      ? 'bg-green-400 animate-pulse' 
                      : 'bg-gray-600'
                  }`}></div>
                  <span className={`text-xs ${
                    retrofitProgress > (index + 1) * 30 
                      ? 'text-green-400' 
                      : 'text-gray-500'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className={`p-2 transform transition-all duration-900 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl p-2 text-center backdrop-blur-sm border border-gray-700/30 hover:border-gray-600/50 transition-all duration-300 group">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl mx-auto mb-1 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
            <i className="ri-customer-service-2-line text-xs text-white animate-bounce-slow"></i>
          </div>
          <p className="text-gray-400 text-xs mb-1 font-medium">Need Support?</p>
          <button className="text-orange-400 hover:text-orange-300 text-xs font-semibold transition-all duration-300 hover:scale-105 bg-orange-500/10 hover:bg-orange-500/20 px-2 py-1 rounded-lg border border-orange-500/30 hover:border-orange-500/50 whitespace-nowrap">
            Contact Expert
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeftNavigation;
